import React, { useEffect, useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, PieLabelRenderProps
} from 'recharts';
import { Transaction, Store, Product } from '../types';
import { Download, TrendingUp, ShoppingCart, BarChart2, Package } from 'lucide-react';
import { getApiBase } from '../utils/apiBase';

const primary = '#F1561D';

const toLocalDateString = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const Analytics: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<'week' | 'month' | 'year'>('week');
  const [reportDate, setReportDate] = useState<string>(toLocalDateString(new Date()));

  const API_BASE = getApiBase();
  const token = localStorage.getItem('authToken');
  const user = JSON.parse(localStorage.getItem('authUser') || '{}');
  const isManager = user.role === 'manager';
  const managerStoreId = user.storeId;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let allTxs: Transaction[] = [];
        let page = 1;
        let hasMore = true;
        while (hasMore) {
          const txRes = await fetch(`${API_BASE}/api/transactions?page=${page}&limit=20`, { headers: { Authorization: `Bearer ${token}` } });
          if (!txRes.ok) throw new Error('Failed');
          const txData = await txRes.json();
          const pageTxs: Transaction[] = txData.data || [];
          allTxs = allTxs.concat(pageTxs);
          hasMore = txData.meta?.hasNextPage === true;
          page++;
          if (allTxs.length >= 2000) break;
        }
        // ── Manager filter: only show their store's transactions ──
        if (isManager && managerStoreId) {
          allTxs = allTxs.filter(tx => {
            const txStoreId = typeof tx.storeId === 'object' ? (tx.storeId as any)?._id : tx.storeId;
            return txStoreId === managerStoreId;
          });
        }
        setTransactions(allTxs);

        const storeRes = await fetch(`${API_BASE}/api/stores`, { headers: { Authorization: `Bearer ${token}` } });
        const storeData = await storeRes.json();
        let allStores: Store[] = storeData.data || [];
        // ── Manager filter: only fetch products for their store ──
        if (isManager && managerStoreId) {
          allStores = allStores.filter(s => s._id === managerStoreId);
        }

        const productsArray: Product[] = [];
        for (const store of allStores) {
          try {
            const res = await fetch(`${API_BASE}/api/products?storeId=${store._id}`, { headers: { Authorization: `Bearer ${token}` } });
            const prodData = await res.json();
            if (Array.isArray(prodData.data)) productsArray.push(...prodData.data);
          } catch (err) { console.error(err); }
        }
        setProducts(productsArray);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    if (token && API_BASE) fetchData();
  }, [token, API_BASE, isManager, managerStoreId]);

  const filteredTransactions = useMemo(() => {
    const now = new Date();
    return transactions.filter(tx => {
      const txDate = new Date(tx.createdAt);
      if (range === 'week') { const ago = new Date(); ago.setDate(now.getDate() - 7); return txDate >= ago; }
      if (range === 'month') return txDate.getMonth() === now.getMonth() && txDate.getFullYear() === now.getFullYear();
      if (range === 'year') return txDate.getFullYear() === now.getFullYear();
      return true;
    });
  }, [transactions, range]);

  const dailyTransactions = useMemo(() => {
    if (!reportDate) return [];
    return transactions.filter(tx => {
      if (!tx.createdAt) return false;
      const localDate = toLocalDateString(new Date(tx.createdAt));
      const utcDate = tx.createdAt.slice(0, 10);
      return localDate === reportDate || utcDate === reportDate;
    });
  }, [transactions, reportDate]);

  const dailySalesData = useMemo(() => {
    const salesByDate: { [key: string]: number } = {};
    filteredTransactions.filter(tx => tx.status === 'completed').forEach(tx => {
      const date = new Date(tx.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      salesByDate[date] = (salesByDate[date] || 0) + tx.total;
    });
    return Object.entries(salesByDate)
      .map(([date, sales]) => ({ date, sales: Math.round(sales) }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [filteredTransactions]);

  const categoryData = useMemo(() => {
    const categoryMap: { [key: string]: number } = {};
    const categoryColors: { [key: string]: string } = {
      'Bakery': '#3182CE', 'Dairy': '#38A169', 'Groceries': '#FF6B35',
      'Cooking': '#805AD5', 'Beverages': '#DD6B20', 'Snacks': '#D53F8C',
      'Frozen Foods': '#E53E3E', 'Household': '#4299E1', 'Personal Care': '#9F7AEA', 'Other': '#718096'
    };
    filteredTransactions.filter(tx => tx.status === 'completed').forEach(tx => {
      tx.items.forEach(item => {
        const product = products.find(p => p._id === item.productId);
        const category = product?.category || 'Other';
        categoryMap[category] = (categoryMap[category] || 0) + item.price * item.qty;
      });
    });
    return Object.entries(categoryMap)
      .map(([name, value]) => ({ name, value: Math.round(value), color: categoryColors[name] || '#718096' }))
      .sort((a, b) => b.value - a.value);
  }, [filteredTransactions, products]);

  const metrics = useMemo(() => {
    const completed = filteredTransactions.filter(tx => tx.status === 'completed');
    const totalRevenue = completed.reduce((sum, tx) => sum + tx.total, 0);
    const avgTransaction = completed.length > 0 ? totalRevenue / completed.length : 0;
    const totalItems = completed.reduce((sum, tx) => sum + tx.items.reduce((s, i) => s + i.qty, 0), 0);
    const itemsPerTransaction = completed.length > 0 ? totalItems / completed.length : 0;
    const conversionRate = filteredTransactions.length > 0 ? (completed.length / filteredTransactions.length) * 100 : 0;
    return { avgTransaction, conversionRate, itemsPerTransaction, completedCount: completed.length, totalRevenue };
  }, [filteredTransactions]);

  const productSalesData = useMemo(() =>
    [...products].filter(p => (p.sales ?? 0) > 0).sort((a, b) => (b.sales ?? 0) - (a.sales ?? 0)), [products]);

  const totalUnitsSold = useMemo(() => products.reduce((sum, p) => sum + (p.sales ?? 0), 0), [products]);
  const totalProductRevenue = useMemo(() => products.reduce((sum, p) => sum + (p.price ?? 0) * (p.sales ?? 0), 0), [products]);
  const topSeller = productSalesData[0] ?? null;

  const dailyCompleted = dailyTransactions.filter(tx => tx.status === 'completed');
  const dailyRevenue = dailyCompleted.reduce((sum, tx) => sum + tx.total, 0);

  const downloadDailyReport = () => {
    const completedOnDate = dailyCompleted;
    const dailyItemCount = completedOnDate.reduce((sum, tx) => sum + tx.items.reduce((s, i) => s + i.qty, 0), 0);
    const itemSalesMap: { [key: string]: { name: string; category: string; barcode: string; price: number; qty: number } } = {};
    completedOnDate.forEach(tx => {
      tx.items.forEach(item => {
        const product = products.find(p => p._id === item.productId);
        const key = item.productId || product?.barcode || String(item.price);
        if (!itemSalesMap[key]) itemSalesMap[key] = { name: product?.name || 'Unknown', category: product?.category || '—', barcode: product?.barcode || '—', price: item.price, qty: 0 };
        itemSalesMap[key].qty += item.qty;
      });
    });
    const itemRows = Object.values(itemSalesMap).sort((a, b) => b.qty - a.qty).map((item, i) => [i + 1, item.name, item.category, item.barcode, item.price.toFixed(2), item.qty, (item.price * item.qty).toFixed(2)]);
    const dateLabel = new Date(reportDate + 'T00:00:00').toLocaleDateString('en-ZA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const rows = [
      ['Daily Sales Report'], ['Date', dateLabel], ['Generated', new Date().toLocaleString()], [],
      ['SUMMARY'], ['Completed Transactions', completedOnDate.length], ['Total Items Sold', dailyItemCount],
      ['Total Revenue', `R ${dailyRevenue.toFixed(2)}`],
      ['Average Transaction Value', completedOnDate.length > 0 ? `R ${(dailyRevenue / completedOnDate.length).toFixed(2)}` : 'R 0.00'],
      [], ['ITEMS SOLD'], ['Rank', 'Product', 'Category', 'Barcode', 'Unit Price (R)', 'Qty Sold', 'Revenue (R)'], ...itemRows,
      [], ['TRANSACTIONS'], ['Reference', 'Time', 'Customer', 'Items', 'Total (R)', 'Status', 'Payment'],
      ...completedOnDate.map(tx => [tx.reference, new Date(tx.createdAt).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }), tx.customer?.name || '—', tx.items.reduce((s, i) => s + i.qty, 0), tx.total.toFixed(2), tx.status, tx.paymentMethod]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a'); link.href = url; link.download = `daily_report_${reportDate}.csv`; link.click(); URL.revokeObjectURL(url);
  };

  const renderCustomizedLabel = (props: PieLabelRenderProps) => {
    const { cx = 0, cy = 0, midAngle = 0, innerRadius = 0, outerRadius = 0, percent } = props;
    if (!percent || percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = +innerRadius + (+outerRadius - +innerRadius) * 0.5;
    const x = +cx + radius * Math.cos(-+midAngle * RADIAN);
    const y = +cy + radius * Math.sin(-+midAngle * RADIAN);
    return <text x={x} y={y} fill="white" textAnchor={x > +cx ? 'start' : 'end'} dominantBaseline="central" fontSize={11} fontWeight="bold">{`${(percent * 100).toFixed(0)}%`}</text>;
  };

  const sel: React.CSSProperties = { border: '1px solid #e5e7eb', borderRadius: '8px', padding: '7px 12px', fontSize: '13px', color: '#374151', outline: 'none', background: '#fff', cursor: 'pointer' };

  if (loading) return (
    <div style={{ padding: '32px', background: '#f8f7f5', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', sans-serif" }}>
      <p style={{ color: '#9ca3af', fontSize: '16px' }}>Loading analytics…</p>
    </div>
  );

  if (transactions.length === 0 && products.length === 0) return (
    <div style={{ padding: '32px', background: '#f8f7f5', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" }}>
      <h1 style={{ margin: '0 0 8px', fontSize: '28px', fontWeight: 600, color: '#111827' }}>Analytics</h1>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '60px', textAlign: 'center', marginTop: '24px' }}>
        <BarChart2 size={40} style={{ margin: '0 auto 12px', display: 'block', color: '#d1d5db' }} />
        <p style={{ margin: 0, fontSize: '16px', color: '#9ca3af' }}>No data yet</p>
        <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#9ca3af' }}>Start making sales to see analytics</p>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '32px', background: '#f8f7f5', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap'); @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@500&display=swap');`}</style>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600, color: '#111827' }}>Analytics</h1>
          <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#9ca3af' }}>
            {isManager ? 'Your store performance' : 'All stores · Sales insights'}
          </p>
        </div>

        {/* Daily report strip */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#fff', border: '1px solid #f3f4f6', borderRadius: '12px', padding: '10px 16px' }}>
          <span style={{ fontSize: '12px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>Daily Report</span>
          <input type="date" value={reportDate} max={toLocalDateString(new Date())} onChange={e => setReportDate(e.target.value)} style={{ ...sel, padding: '6px 10px', fontSize: '13px' }} />
          <span style={{ fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap' }}>{dailyCompleted.length} txns · R {dailyRevenue.toFixed(2)}</span>
          <button onClick={downloadDailyReport} disabled={dailyCompleted.length === 0}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', background: dailyCompleted.length === 0 ? '#e5e7eb' : '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', padding: '7px 14px', fontSize: '13px', fontWeight: 500, cursor: dailyCompleted.length === 0 ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
            <Download size={14} /> Download
          </button>
        </div>
      </div>

      {/* ── Pillars ───────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
        {[
          { label: 'Total Units Sold', value: totalUnitsSold.toLocaleString(), sub: 'Across all products', accent: '#fb923c', icon: <Package size={18} color="#fb923c" /> },
          { label: 'Product Revenue', value: `R ${totalProductRevenue.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, sub: 'Price × units sold', accent: '#4ade80', icon: <TrendingUp size={18} color="#4ade80" /> },
          { label: 'Top Seller', value: topSeller?.name ?? '—', sub: topSeller ? `${topSeller.sales} units sold` : 'No sales yet', accent: '#60a5fa', icon: <ShoppingCart size={18} color="#60a5fa" /> },
        ].map(card => (
          <div key={card.label} style={{ background: '#fff', borderRadius: '14px', borderTop: `4px solid ${card.accent}`, padding: '28px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '8px', border: '1px solid #f3f4f6' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: '#f8f7f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{card.icon}</div>
            <p style={{ margin: 0, fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{card.label}</p>
            <p style={{ margin: 0, fontSize: card.label === 'Top Seller' ? '22px' : '40px', fontWeight: 700, color: '#111827', lineHeight: 1.1, fontFamily: card.label === 'Top Seller' ? 'inherit' : "'DM Mono', monospace" }}>{card.value}</p>
            <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>{card.sub}</p>
          </div>
        ))}
      </div>

      {/* ── Metric cards ──────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'Transaction Revenue', value: `R ${metrics.totalRevenue.toFixed(2)}`, sub: `${metrics.completedCount} completed`, accent: primary },
          { label: 'Avg Transaction', value: `R ${metrics.avgTransaction.toFixed(2)}`, sub: 'Per transaction', accent: '#FF6B35' },
          { label: 'Success Rate', value: `${metrics.conversionRate.toFixed(1)}%`, sub: 'Of all transactions', accent: '#3182CE' },
          { label: 'Avg Items', value: metrics.itemsPerTransaction.toFixed(1), sub: 'Per transaction', accent: '#805AD5' },
        ].map(card => (
          <div key={card.label} style={{ background: '#fff', borderRadius: '14px', borderTop: `4px solid ${card.accent}`, padding: '20px', border: '1px solid #f3f4f6' }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>{card.label}</p>
            <p style={{ margin: '6px 0 0', fontSize: '26px', fontWeight: 700, color: '#111827', lineHeight: 1 }}>{card.value}</p>
            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#9ca3af' }}>{card.sub}</p>
          </div>
        ))}
      </div>

      {/* ── Charts ────────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
        {/* Sales trend */}
        <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', border: '1px solid #f3f4f6', borderTop: `4px solid ${primary}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#111827' }}>Sales Trend</h4>
            <select value={range} onChange={e => setRange(e.target.value as 'week' | 'month' | 'year')} style={sel}>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </select>
          </div>
          {dailySalesData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={dailySalesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => [`R ${Number(v).toFixed(2)}`, 'Sales']} contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontSize: '13px' }} />
                <Bar dataKey="sales" fill={primary} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '14px' }}>No data for selected period</div>
          )}
        </div>

        {/* Category pie */}
        <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', border: '1px solid #f3f4f6', borderTop: `4px solid ${primary}` }}>
          <h4 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 600, color: '#111827' }}>Sales by Category</h4>
          {categoryData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={categoryData} cx="50%" cy="50%" labelLine={false} label={renderCustomizedLabel} outerRadius={95} dataKey="value">
                    {categoryData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => [`R ${typeof v === 'number' ? v.toFixed(2) : '0.00'}`, 'Sales']} contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontSize: '13px' }} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginTop: '12px' }}>
                {categoryData.map((cat, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: cat.color, flexShrink: 0 }} />
                    <span style={{ color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{cat.name}</span>
                    <span style={{ color: '#9ca3af', marginLeft: 'auto' }}>R{cat.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '14px' }}>No category data</div>
          )}
        </div>
      </div>

      {/* ── Product movement table ─────────────────────────────────────────── */}
      {productSalesData.length > 0 && (
        <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', border: '1px solid #f3f4f6', borderTop: `4px solid ${primary}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#111827' }}>Product Movement</h4>
            <span style={{ fontSize: '13px', color: '#9ca3af' }}>{productSalesData.length} products sold</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                  {['Rank', 'Product', 'Category', 'Barcode', 'Price', 'Units Sold', 'Revenue', '% of Sales'].map((h, i) => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: i >= 4 ? 'right' : 'left', fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {productSalesData.map((product, index) => {
                  const revenue = (product.price ?? 0) * (product.sales ?? 0);
                  const pct = totalUnitsSold > 0 ? ((product.sales ?? 0) / totalUnitsSold) * 100 : 0;
                  const rankStyle: Record<number, React.CSSProperties> = {
                    0: { background: '#fef9c3', color: '#854d0e' },
                    1: { background: '#f3f4f6', color: '#374151' },
                    2: { background: '#fff5f2', color: '#c2410c' },
                  };
                  return (
                    <tr key={product._id} style={{ borderBottom: '1px solid #f9fafb' }}
                      onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = '#fafafa'}
                      onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'}>
                      <td style={{ padding: '12px' }}>
                        <div style={{ width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, ...(rankStyle[index] ?? { color: '#9ca3af' }) }}>
                          {index + 1}
                        </div>
                      </td>
                      <td style={{ padding: '12px', fontWeight: 500, color: '#111827' }}>{product.name}</td>
                      <td style={{ padding: '12px', color: '#6b7280' }}>{product.category}</td>
                      <td style={{ padding: '12px', color: '#9ca3af', fontFamily: 'monospace', fontSize: '12px' }}>{product.barcode ?? '—'}</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#374151' }}>R {typeof product.price === 'number' ? product.price.toFixed(2) : product.price}</td>
                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#ea580c' }}>{product.sales ?? 0}</td>
                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#16a34a' }}>R {revenue.toFixed(2)}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                          <div style={{ width: '56px', height: '4px', background: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', background: primary, borderRadius: '4px', width: `${Math.min(pct, 100)}%` }} />
                          </div>
                          <span style={{ fontSize: '12px', color: '#9ca3af', minWidth: '38px', textAlign: 'right' }}>{pct.toFixed(1)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #f3f4f6', background: '#fafafa' }}>
                  <td colSpan={5} style={{ padding: '12px', fontWeight: 600, color: '#374151' }}>Total</td>
                  <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700, color: '#ea580c' }}>{totalUnitsSold}</td>
                  <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>R {totalProductRevenue.toFixed(2)}</td>
                  <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700, color: '#9ca3af' }}>100%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
